import { Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import type { EidEasySigningModalProps } from './EidEasySigningModal.types';
import { EidEasySigningSurface } from './EidEasySigningSurface';
import { SigningPanel } from './SigningPanel';

// Witnesses cannot use OTP — surfaces that may render the modal for a witness
// signer must pass an explicit `allowedMethods` that omits OTP.
const ALL_METHODS = [
	EidEasySigningMethod.SMART_ID,
	EidEasySigningMethod.FTN,
	EidEasySigningMethod.DRAW,
	EidEasySigningMethod.OTP,
];

export const EidEasySigningModal = ({
	status,
	isSigningInProgress,
	isModalOpen,
	fileId,
	fileName,
	allowedMethods,
	onClose,
	onExitTransitionEnd,
	panelOverride,
	panelHeader,
	...rest
}: EidEasySigningModalProps) => {
	const tCommonUi = useTranslationCommonUi();
	const isMobile = useMediaQuery('(max-width: 768px)') ?? false;
	const methods = allowedMethods ?? ALL_METHODS;

	const hasSurface = !!(fileId && fileName);

	return (
		<Modal
			opened={isModalOpen}
			onClose={onClose}
			onExitTransitionEnd={onExitTransitionEnd}
			title={tCommonUi('eid_easy_signing.modal_title')}
			centered
			closeOnClickOutside={!isSigningInProgress}
			closeOnEscape={!isSigningInProgress}
			withCloseButton={!isSigningInProgress}
			closeButtonProps={{
				icon: (
					<IconX
						size={18}
						stroke={1.5}
						data-testid={TEST_ELEMENT_IDS.EID_EASY_SIGNING_MODAL_CLOSE_BUTTON}
					/>
				),
			}}
			size={hasSurface ? '95%' : 'lg'}
			fullScreen={isMobile}
			radius={hasSurface && !isMobile ? 16 : undefined}
			styles={
				hasSurface
					? {
							content: {
								maxWidth: '1600px',
								height: '90vh',
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden',
							},
							body: {
								padding: 0,
								flex: '1 1 auto',
								overflow: 'hidden',
								minHeight: 0,
							},
						}
					: undefined
			}
		>
			{hasSurface ? (
				<EidEasySigningSurface
					{...rest}
					fileId={fileId}
					fileName={fileName}
					status={status}
					allowedMethods={methods}
					onClose={onClose}
					panelOverride={panelOverride}
					panelHeader={panelHeader}
				/>
			) : (
				panelOverride ?? (
					<>
						{panelHeader}
						<SigningPanel
							{...rest}
							status={status}
							allowedMethods={methods}
							onClose={onClose}
						/>
					</>
				)
			)}
		</Modal>
	);
};
